import { type IMetaTotal } from '@/types/IBannerData';
import { type IErrorData } from '@/utils/getErrorData';
import { createSlice } from '@reduxjs/toolkit';
import {
  getQuestionsThunk,
  updateQuestionThunk,
  updateQuestionStatusThunk,
  updateQuestionAnswerStatusThunk,
  createQuestionAnswerThunk
} from './questionsThunks';
import { type IQuestion } from '@/types/IReviewsAndQuestion';

export interface IQuestionsState {
  questions: IQuestion[];
  meta: IMetaTotal;
  isLoading: boolean;
  error: IErrorData | null;
}

const initialState: IQuestionsState = {
  questions: [],
  meta: { total: 0 },
  isLoading: false,
  error: null
};

const questions = createSlice({
  name: 'questions',
  initialState,
  reducers: {
    resetQuestions(state) {
      state.questions = initialState.questions;
      state.meta = initialState.meta;
      state.isLoading = initialState.isLoading;
      state.error = initialState.error;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuestionsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getQuestionsThunk.fulfilled, (state, action) => {
        state.questions = action.payload.items;
        state.meta = action.payload.meta;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getQuestionsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при получении вопросов'
        };
      })
      .addCase(updateQuestionThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQuestionThunk.fulfilled, (state, action) => {
        const index = state.questions.findIndex(
          (question) => question.id === action.payload.id
        );
        if (index !== -1) {
          state.questions[index] = action.payload;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateQuestionThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при обновлении вопроса'
        };
      })
      .addCase(updateQuestionStatusThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQuestionStatusThunk.fulfilled, (state, action) => {
        const index = state.questions.findIndex(
          (question) => question.id === action.payload.id
        );
        if (index !== -1) {
          state.questions[index] = action.payload;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateQuestionStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при обновлении статуса вопроса'
        };
      })
      .addCase(updateQuestionAnswerStatusThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQuestionAnswerStatusThunk.fulfilled, (state, action) => {
        const index = state.questions.findIndex(
          (question) => question.id === action.payload.id
        );
        if (index !== -1) {
          state.questions[index] = action.payload;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateQuestionAnswerStatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при обновлении статуса ответа'
        };
      })
      .addCase(createQuestionAnswerThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createQuestionAnswerThunk.fulfilled, (state, action) => {
        const index = state.questions.findIndex(
          (question) => question.id === action.payload.id
        );
        if (index !== -1) {
          state.questions[index] = action.payload;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createQuestionAnswerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? {
          message: 'Ошибка при создании ответа'
        };
      });
  }
});

export const { resetQuestions } = questions.actions;
export default questions.reducer;
